/**
 * Universal FHEVM SDK - CDN Version
 * Uses window.RelayerSDK from CDN script
 *
 * IMPORTANT: This requires the CDN script in index.html:
 * <script src="https://cdn.zama.org/relayer-sdk-js/0.3.0-5/relayer-sdk-js.umd.cjs"></script>
 */
let fheInstance = null;
/**
 * Initialize FHEVM instance
 */
export async function initializeFheInstance(options) {
    if (typeof window !== 'undefined' && window.ethereum) {
        return initializeBrowserFheInstance();
    }
    else {
        throw new Error('Browser environment with MetaMask required');
    }
}
/**
 * Initialize FHEVM instance for browser environment
 * Uses CDN-loaded RelayerSDK with CORRECT SepoliaConfig
 */
async function initializeBrowserFheInstance() {
    if (typeof window === 'undefined' || !window.ethereum) {
        throw new Error('Ethereum provider not found. Please install MetaMask.');
    }
    // Get CDN-loaded SDK
    const sdk = window.RelayerSDK || window.relayerSDK;
    if (!sdk) {
        throw new Error('RelayerSDK not loaded! Add this to index.html:\n' +
            '<script src="https://cdn.zama.org/relayer-sdk-js/0.3.0-5/relayer-sdk-js.umd.cjs"></script>');
    }
    const { initSDK, createInstance, SepoliaConfig } = sdk;
    // Step 1: Initialize SDK (REQUIRED!)
    await initSDK();
    console.log('✅ FHEVM SDK initialized');
    // Step 2: Verify Sepolia network
    const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
    const chainId = parseInt(chainIdHex, 16);
    console.log('🔗 Chain ID:', chainId);
    if (chainId !== 11155111) {
        throw new Error(`Please switch to Sepolia network! Current: ${chainId}, Required: 11155111`);
    }
    // Step 3: Create instance with CORRECT config
    // CDN SDK expects: { ...SepoliaConfig, network: provider }
    const config = {
        ...SepoliaConfig,
        network: window.ethereum
    };
    console.log('🔧 Creating FHEVM instance...');
    console.log('📋 SepoliaConfig keys:', Object.keys(SepoliaConfig || {}));
    try {
        fheInstance = await createInstance(config);
        console.log('✅ FHEVM instance created!');
        return fheInstance;
    }
    catch (err) {
        console.error('❌ FHEVM creation failed:', err);
        console.error('📋 Config used:', JSON.stringify(config, null, 2));
        throw err;
    }
}
export function getFheInstance() {
    return fheInstance;
}
/**
 * Create encrypted input for contract interaction
 */
export async function createEncryptedInput(contractAddress, userAddress, value) {
    const fhe = getFheInstance();
    if (!fhe)
        throw new Error('FHE instance not initialized');
    console.log(`🔐 Encrypting value ${value}...`);
    const inputHandle = fhe.createEncryptedInput(contractAddress, userAddress);
    inputHandle.add32(value);
    const result = await inputHandle.encrypt();
    console.log('✅ Encryption complete');
    if (result?.handles?.length > 0) {
        return {
            encryptedData: result.handles[0],
            proof: result.inputProof
        };
    }
    return {
        encryptedData: result.encryptedData || result,
        proof: result.proof || result.inputProof || result
    };
}
/**
 * Decrypt value using EIP-712 signature
 */
export async function decryptValue(encryptedBytes, contractAddress, signer) {
    const fhe = getFheInstance();
    if (!fhe)
        throw new Error('FHE instance not initialized');
    console.log('🔐 Decrypting...');
    const keypair = fhe.generateKeypair();
    const handleContractPairs = [{
            handle: encryptedBytes,
            contractAddress: contractAddress,
        }];
    const startTimeStamp = Math.floor(Date.now() / 1000).toString();
    const durationDays = "10";
    const contractAddresses = [contractAddress];
    const eip712 = fhe.createEIP712(keypair.publicKey, contractAddresses, startTimeStamp, durationDays);
    const signature = await signer.signTypedData(eip712.domain, { UserDecryptRequestVerification: eip712.types.UserDecryptRequestVerification }, eip712.message);
    const result = await fhe.userDecrypt(handleContractPairs, keypair.privateKey, keypair.publicKey, signature.replace("0x", ""), contractAddresses, await signer.getAddress(), startTimeStamp, durationDays);
    return Number(result[encryptedBytes]);
}
/**
 * Public decryption
 */
export async function publicDecrypt(encryptedBytes) {
    const fhe = getFheInstance();
    if (!fhe)
        throw new Error('FHE instance not initialized');
    const result = await fhe.publicDecrypt([encryptedBytes]);
    if (result?.clearValues) {
        const value = result.clearValues[encryptedBytes];
        return typeof value === 'bigint' ? Number(value) : Number(value);
    }
    return Number(result[encryptedBytes] || Object.values(result)[0]);
}
// Legacy exports
export const encryptValue = async (contractAddress, address, plainDigits) => {
    const fhe = getFheInstance();
    if (!fhe)
        throw new Error("FHEVM not initialized");
    const inputHandle = fhe.createEncryptedInput(contractAddress, address);
    for (const d of plainDigits)
        inputHandle.add8(d);
    return await inputHandle.encrypt();
};
export const requestUserDecryption = decryptValue;
export const batchDecryptValues = async (handles, contractAddress, signer) => {
    const results = {};
    for (const handle of handles) {
        results[handle] = await decryptValue(handle, contractAddress, signer);
    }
    return results;
};
export const decryptMultipleHandles = async (contractAddress, signer, handles) => {
    const fhe = getFheInstance();
    if (!fhe)
        throw new Error("FHEVM not initialized");
    const result = await fhe.publicDecrypt(handles);
    const values = handles.map((h) => Number(result?.clearValues?.[h] || result[h]));
    return {
        cleartexts: result?.abiEncodedClearValues || '',
        decryptionProof: result?.decryptionProof || '',
        values
    };
};
export const fetchPublicDecryption = async (handles) => {
    const fhe = getFheInstance();
    if (!fhe)
        throw new Error("FHEVM not initialized");
    return fhe.publicDecrypt(handles);
};
